import React from 'react';
import { User, Mail, Shield, Building2, Calendar, CheckCircle, XCircle } from 'lucide-react';

const UserDetail = ({ user }) => {
  const DetailRow = ({ icon: Icon, label, value, valueClassName = '' }) => (
    <div className="py-3 border-b border-gray-200 last:border-0">
      <div className="flex items-start">
        <Icon className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
        <div className="flex-1">
          <dt className="text-sm font-medium text-gray-500">{label}</dt>
          <dd className={`mt-1 text-sm ${valueClassName || 'text-gray-900'}`}>
            {value || 'N/A'}
          </dd>
        </div>
      </div>
    </div>
  );

  const getRolLabel = (rol) => {
    const roles = {
      superadmin: 'Super Administrador',
      admin_org: 'Administrador de Organización',
      miembro: 'Miembro',
    };
    return roles[rol] || rol;
  };

  const getRolBadge = (rol) => {
    const badges = {
      superadmin: 'bg-purple-100 text-purple-800',
      admin_org: 'bg-blue-100 text-blue-800',
      miembro: 'bg-gray-100 text-gray-800',
    };
    return badges[rol] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header con estado */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-full bg-primary-600 flex items-center justify-center">
            <span className="text-xl font-bold text-white">
              {user.nombre_completo?.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {user.nombre_completo}
            </h3>
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRolBadge(
                user.rol
              )}`}
            >
              {getRolLabel(user.rol)}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {user.activo ? (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
              <CheckCircle className="h-4 w-4 mr-1" />
              Activo
            </span>
          ) : (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
              <XCircle className="h-4 w-4 mr-1" />
              Inactivo
            </span>
          )}
        </div>
      </div>

      {/* Información básica */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">
          Información Básica
        </h4>
        <dl className="divide-y divide-gray-200">
          <DetailRow
            icon={User}
            label="Nombre completo"
            value={user.nombre_completo}
          />
          <DetailRow
            icon={Mail}
            label="Correo electrónico"
            value={user.email}
          />
          <DetailRow
            icon={Shield}
            label="Rol"
            value={
              <span className={`px-2 py-1 rounded text-xs font-medium ${getRolBadge(user.rol)}`}>
                {getRolLabel(user.rol)}
              </span>
            }
          />
          <DetailRow
            icon={Building2}
            label="Organización"
            value={user.nombre_organizacion || 'Sin organización'}
          />
        </dl>
      </div>

      {/* Información de auditoría */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">
          Información de Auditoría
        </h4>
        <dl className="divide-y divide-gray-200">
          <DetailRow
            icon={Calendar}
            label="Fecha de creación"
            value={formatDate(user.fecha_creacion)}
          />
          <DetailRow
            icon={Calendar}
            label="Última actualización"
            value={formatDate(user.fecha_actualizacion)}
          />
        </dl>
      </div>

      {/* Estadísticas (placeholder) */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">
          Actividad
        </h4>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">0</p>
            <p className="text-xs text-gray-600">Evaluaciones</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">0</p>
            <p className="text-xs text-gray-600">Encuestas</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">0</p>
            <p className="text-xs text-gray-600">Denuncias</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDetail;
